/**
 * CartDrawer.jsx — Drawer latéral droit du panier.
 *
 * §11/§18 — Composant primitif ; importé par CartIcon et pages consommatrices.
 * §12 sexies — Croix de fermeture cerclée en haut à droite du header.
 * §20 — Tokens uniquement depuis theme.js.
 * §12 ter — Zéro px en dur pour espacements/polices.
 * §12 undecies — Casse grammaticale.
 * §12 terdecies — Aucun composant natif navigateur.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import {
  closeCart,
  clearCart,
  selectCartItems,
  selectCartCount,
  selectCartIsOpen,
  selectCartTotal,
} from '../../../store/slices/cartSlice';
import { CtaButton, GhostButton } from '../daComponents';
import {COLORS, FONTS, RADII, SHADOW, media, FONT_SIZE, FONT_WEIGHT} from '../theme';
import { formatUSD } from '../../../utils/formatPrice';
import { CartItemRow } from './CartItemRow';

/* ─── Animations ────────────────────────────────────────────────────────────── */

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ─── Styled components ─────────────────────────────────────────────────────── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${COLORS.overlay};
  /* Au-dessus du header. Sur les pages DA le header est en z-index 1030 (solidnav) donc
     1100 suffisait, mais sur la carte (/tile-map, /tile-map-v2) le header est le .navbar
     de base en z-index 2000 → le panier passait dessous. 2100 le met au-dessus du header
     (2000) et du panneau parcelle (2001), en restant sous les toasts (3000) et modales (9999). */
  z-index: 2100;
  animation: ${fadeIn} 0.2s ease;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 22.5rem; /* 360px */
  max-width: 100vw;
  background: ${COLORS.white};
  box-shadow: ${SHADOW.modal};
  /* cf. Overlay : au-dessus du header carte (2000) et du panneau parcelle (2001), sous toasts/modales. */
  z-index: 2101;
  display: flex;
  flex-direction: column;
  border-radius: ${RADII.card} 0 0 ${RADII.card};
  animation: ${css`${slideIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1)`};

  ${media.sm`
    width: 100vw;
    border-radius: 0;
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.25rem;
  background: ${COLORS.accent};
  border-radius: ${RADII.card} 0 0 0;
  flex: 0 0 auto;

  ${media.sm`
    border-radius: 0;
  `}
`;

const HeaderTitle = styled.h2`
  font-family: ${FONTS.head};
  font-size: 1.05rem;
  font-weight: ${FONT_WEIGHT.extrabold};
  color: ${COLORS.white};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CountBadge = styled.span`
  font-family: ${FONTS.body};
  font-size: 0.8rem;
  font-weight: ${FONT_WEIGHT.normal};
  color: rgba(255, 255, 255, 0.8);
`;

/* §12 sexies — Croix cerclée blanc semi-transparent, border-radius 50% */
const CloseBtn = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: ${RADII.circle};
  border: 0.0625rem solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.2);
  color: ${COLORS.white};
  font-size: 1.15rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
  flex: 0 0 auto;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  &:active {
    transform: scale(0.94);
  }
`;

const ItemList = styled.div`
  flex: 1 1 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* Scrollbar DA (Webkit) */
  &::-webkit-scrollbar {
    width: 0.3rem;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.inkLine};
    border-radius: 0.15rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: ${COLORS.muted};
  font-family: ${FONTS.body};
  gap: 0.75rem;
`;

const EmptyIcon = () => (
  <svg
    width="3rem"
    height="3rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ color: 'rgba(13, 10, 16, 0.25)' }}
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const EmptyText = styled.p`
  font-size: 0.9rem;
  margin: 0;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  padding: 1rem 1.25rem;
  border-top: 0.0625rem solid ${COLORS.inkLine};
  background: ${COLORS.white};
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-family: ${FONTS.head};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
`;

const TotalAmount = styled.span`
  font-family: ${FONTS.mono};
  font-size: 1.1rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.accent};
`;

const CheckoutBtn = styled(CtaButton)`
  width: 100%;
  padding: 0.8rem 1.2rem;
  font-size: 0.9rem;
`;

const ClearBtn = styled(GhostButton)`
  width: 100%;
  padding: 0.6rem 1.1rem;
  font-size: 0.85rem;
  color: ${COLORS.muted};
`;

/* ─── Component ─────────────────────────────────────────────────────────────── */

/**
 * CartDrawer — Drawer latéral droit du panier.
 *
 * @param {Function} onCheckout - callback déclenché au clic "Proceed to Checkout"
 */
export function CartDrawer({ onCheckout }) {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectCartIsOpen);
  const items = useSelector(selectCartItems);
  const count = useSelector(selectCartCount);
  const total = useSelector(selectCartTotal);

  if (!isOpen) return null;

  const handleClose = () => dispatch(closeCart());
  const handleClear = () => dispatch(clearCart());

  const handleCheckout = () => {
    if (onCheckout) onCheckout();
  };

  const formattedTotal = formatUSD(total);

  /* §69 — Portal vers document.body : le drawer (position:fixed) est rendu hors de
     l'arbre de la page courante. Sur une page normale (sous <Header>) le rendu est
     identique ; en mode fullpage (AssetFullPage : conteneur 100vh overflow:hidden +
     enfants backdrop-filter, qui créent stacking-context / containing-block), le portal
     garantit que l'overlay + le drawer ne sont jamais clippés ni piégés par un ancêtre.
     Un seul lieu corrigé → tous les consommateurs (Header, fullpage) en bénéficient (DRY §9). */
  return createPortal(
    <>
      <Overlay onClick={handleClose} aria-hidden="true" />

      <Drawer role="dialog" aria-modal="true" aria-label="Shopping cart" data-testid="cart-drawer">
        {/* ── Header ── */}
        <Header>
          <HeaderTitle>
            Cart
            <CountBadge>({count} {count === 1 ? 'item' : 'items'})</CountBadge>
          </HeaderTitle>
          <CloseBtn
            type="button"
            aria-label="Close cart"
            onClick={handleClose}
          >
            ×
          </CloseBtn>
        </Header>

        {/* ── Item list ── */}
        <ItemList>
          {items.length === 0 ? (
            <EmptyState>
              <EmptyIcon />
              <EmptyText>Your cart is empty</EmptyText>
            </EmptyState>
          ) : (
            items.map((item) => (
              <CartItemRow
                key={`${item.itemType}-${item.itemId}`}
                item={item}
              />
            ))
          )}
        </ItemList>

        {/* ── Footer — shown only when cart has items ── */}
        {items.length > 0 && (
          <Footer>
            <TotalRow>
              <TotalLabel>Total</TotalLabel>
              <TotalAmount>{formattedTotal}</TotalAmount>
            </TotalRow>
            <CheckoutBtn
              type="button"
              data-testid="cart-proceed-checkout"
              onClick={handleCheckout}
            >
              Proceed to Checkout
            </CheckoutBtn>
            <ClearBtn
              type="button"
              data-testid="cart-clear"
              onClick={handleClear}
            >
              Clear cart
            </ClearBtn>
          </Footer>
        )}
      </Drawer>
    </>,
    document.body
  );
}

export default CartDrawer;
